import React, { useEffect, useRef, useState } from "react";
import { getOrdersWithParams } from "../../../services/orderService";
import { useNavigate } from "react-router-dom";
import ToolTip from "../../../components/component-tools/ToolTip";
import { usePendingOrdersContext } from "../../../contexts/PendingOrdersContext";
import { formatDate } from "../../../services/helpers";

const PendingOrders = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const navigate = useNavigate();
  const observer = useRef<IntersectionObserver | null>(null);
  const lastOrderRef = useRef<HTMLDivElement | null>(null);
  type SortMode = "updated" | "lastName";
  const [sortMode, setSortMode] = useState<SortMode>("updated");
  const [sortDisabled, setSortDisabled] = useState(true);
  const {
    pendingOrders,
    setPendingOrders,
    pageNumber,
    setPageNumber,
    searchTerm,
    setSearchTerm,
    searchApplied,
    setSearchApplied,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    orderType,
    setOrderType,
    searchType,
    setSearchType,
    appliedStartDate,
    setAppliedStartDate,
    appliedEndDate,
    setAppliedEndDate,
    appliedOrderType,
    setAppliedOrderType,
    appliedSearchType,
    setAppliedSearchType,
    hasMore,
    setHasMore,
  } = usePendingOrdersContext();

  const handleSearch = () => {
    if (loading || searchTerm.trim() === searchApplied) return;

    setSearchApplied(searchTerm.trim());
    setPendingOrders([]);
    setPageNumber(1);
    setHasMore(true);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchApplied("");
    setPendingOrders([]);
    setPageNumber(1);
    setHasMore(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const loadedSignatureMatches = () =>
    pageNumber === 1 &&
    searchApplied === "" &&
    !appliedStartDate &&
    !appliedEndDate &&
    !appliedOrderType &&
    !appliedSearchType &&
    sortMode === "updated";

  useEffect(() => {
    const fetchOrders = async () => {
      if (pendingOrders.length > 0 && loadedSignatureMatches()) {
        return;
      }

      try {
        setSortDisabled(true);
        setLoading(true);

        const { orders, totalRecords } = await getOrdersWithParams(
          1,
          pageNumber,
          10,
          searchApplied,
          appliedStartDate,
          appliedEndDate,
          appliedOrderType,
          appliedSearchType,
          sortMode,
        );

        setPendingOrders((prevOrders) => {
          const existingIds = new Set(prevOrders.map((order) => order.orderID));
          const uniqueOrders = orders.filter(
            (o) => !existingIds.has(o.orderID),
          );
          const combined = [...prevOrders, ...uniqueOrders];

          if (combined.length >= totalRecords) setHasMore(false);
          return combined;
        });
      } catch (error) {
        setErrorMessage("Failed to load orders.");
        console.error("Error fetching orders:", error);
      } finally {
        setTimeout(() => setSortDisabled(false), 3000);
        setLoading(false);
      }
    };

    fetchOrders();
  }, [
    searchApplied,
    appliedStartDate,
    appliedEndDate,
    appliedOrderType,
    appliedSearchType,
    pageNumber,
    sortMode,
  ]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prev) => prev + 1);
        }
      },
      { threshold: 1.0 },
    );

    if (lastOrderRef.current) observer.current.observe(lastOrderRef.current);

    return () => observer.current?.disconnect();
  }, [hasMore, loading]);

  const applyFilter = () => {
    if (startDate && endDate) {
      setAppliedStartDate(startDate);
      setAppliedEndDate(endDate);
    }

    if (orderType) {
      setAppliedOrderType(orderType);
    }

    if (searchType) {
      setAppliedSearchType(searchType);
    }

    setPendingOrders([]);
    setPageNumber(1);
    setHasMore(true);
    setShowFilter(false);
  };

  const resetFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setOrderType(null);
    setAppliedOrderType(null);
    setSearchType(null);
    setAppliedSearchType(null);
    setPendingOrders([]);
    setPageNumber(1);
    setHasMore(true);
    setShowFilter(false);
  };

  const resetAndApplySort = (newSort: SortMode) => {
    setPendingOrders([]);
    setPageNumber(1);
    setHasMore(true);
    setLoading(true);
    setSortMode(newSort);
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1 style={{ display: "flex" }}>
          Pending Orders{" "}
          <ToolTip
            tooltipText={`Candidate has not completed all required order forms. \nBlue: Criminal\nOrange: Reference`}
            placement="right"
            className="info-icon"
          />
        </h1>

        <div className="page-items-container">
          <div className="sort-toggle">
            <button
              className={`btn-funnel ${sortDisabled ? "disabled" : ""}`}
              onClick={() =>
                resetAndApplySort(
                  sortMode === "updated" ? "lastName" : "updated",
                )
              }
              title={
                sortMode === "updated"
                  ? "Sorted by Last Updated"
                  : "Sorted by Last Name"
              }
              disabled={sortDisabled}
            >
              {sortMode === "updated" ? (
                <i className="bi bi-clock-history filter-icon"></i>
              ) : (
                <i className="bi bi-sort-alpha-down filter-icon"></i>
              )}
            </button>
          </div>
          <button
            className="btn-funnel"
            onClick={() => setShowFilter(!showFilter)}
          >
            <i
              className={`bi bi-funnel-fill filter-icon ${
                (appliedStartDate && appliedEndDate) || orderType || searchType
                  ? "active-filter-icon"
                  : ""
              }`}
            ></i>
          </button>

          <div className="search-container">
            <input
              style={{ border: "none", width: "100%", outline: "none" }}
              type="text"
              placeholder="Search Candidate/Client/Reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            {searchTerm.trim() ? (
              <i
                className="bi bi-x-circle clear-icon"
                onClick={handleClearSearch}
                style={{ cursor: loading ? "disabled" : "pointer" }}
              ></i>
            ) : (
              <i
                className="bi bi-search search-icon"
                onClick={handleSearch}
                style={{ cursor: loading ? "disabled" : "pointer" }}
              ></i>
            )}
          </div>
          <button
            className="oxbtn btn-new"
            onClick={() => navigate(`/admin/create-order`)}
          >
            <div> New Order </div>
            <i className="bi bi-plus-circle-fill plus-icon"></i>
          </button>
        </div>
      </div>

      {/* Filter Popup */}
      {showFilter && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowFilter(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowFilter(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Filter Pending Orders</h2>
            </div>
            <div className="filter-body">
              <div className="field-stacked">
                <label className="field-label">Date Range:</label>
                <div className="date-range-inputs">
                  <input
                    className="form-control"
                    type="date"
                    value={startDate || ""}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <span>to</span>
                  <input
                    className="form-control"
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
              <br></br>
              <div className="field-stacked">
                <label className="field-label">Order Type:</label>
                <div className="chip-container">
                  <div
                    className={`chip-date criminal-filter ${
                      orderType === "Criminal" ? "active-filter" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setOrderType("Criminal")}
                  >
                    <div>{"Criminal"}</div>
                  </div>
                  <div
                    className={`chip-date reference-filter ${
                      orderType === "Reference" ? "active-filter" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setOrderType("Reference")}
                  >
                    <div>{"Reference"}</div>
                  </div>
                  <div
                    className={`chip-date ${
                      orderType === "Both" ? "active-filter" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setOrderType("Both")}
                  >
                    <div>{"Both"}</div>
                  </div>
                </div>
              </div>
              <br></br>
              <div className="field-stacked">
                <label className="field-label">Search Type:</label>
                <div className="chip-container">
                  <div
                    className={`chip-date ${
                      searchType === "Candidate" ? "active-filter" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSearchType("Candidate")}
                  >
                    <div>{"Candidate"}</div>
                  </div>
                  <div
                    className={`chip-date ${
                      searchType === "Client" ? "active-filter" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSearchType("Client")}
                  >
                    <div>{"Abbreviation"}</div>
                  </div>
                  <div
                    className={`chip-date ${
                      searchType === "Reference" ? "active-filter" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSearchType("Reference")}
                  >
                    <div>{"Reference"}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="smbtn btn-grey" onClick={resetFilter}>
                Reset
              </button>
              <button
                className={`smbtn btn-green ${
                  (!startDate || !endDate) && !orderType && !searchType
                    ? "disabled"
                    : ""
                }`}
                onClick={applyFilter}
                disabled={(!startDate || !endDate) && !orderType && !searchType}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      {loading && pageNumber === 1 ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          {pendingOrders.length > 0 ? (
            <>
              <div className="orders-list-small">
                {pendingOrders.map((order, index) => (
                  <div
                    key={order.orderID}
                    ref={
                      index === pendingOrders.length - 1 ? lastOrderRef : null
                    }
                    className="order-card-small"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/admin/view-order/${order.orderID}`, {
                        state: { source: "pending" },
                      })
                    }
                  >
                    <div
                      className="space-between-container"
                      style={{ flexWrap: "nowrap" }}
                    >
                      <div className="order-card-small-info">
                        <div className="candidate-name">
                          {(order.applican_link || order.candidat_link) && "*"}
                          {order.candidateFirstName} {order.candidateLastName}{" "}
                          <div className="candidate-ssn">
                            {order.candidateSSN}
                          </div>
                        </div>
                        <div className="order-client">{order.clientName}</div>
                        <div className="order-status-indicator">
                          <div
                            className={`circle-status 
      ${order.hasReferenceOrder ? "has-reference" : "no-reference"} 
      ${order.referenceInAdminReview ? "reference-active" : ""} 
      ${order.hasCriminalOrder ? "has-criminal" : "no-criminal"} 
      ${order.criminalInAdminReview ? "criminal-active" : ""}`}
                          ></div>
                        </div>
                        {order?.alert?.trim() && (
                          <ToolTip
                            tooltipText={`${order.alert}\n${formatDate(order?.alertLastUpdatedDate, "datetime")}`}
                            placement="bottom"
                            className="alert-sticky"
                          >
                            <i className="bi bi-stickies-fill"></i>
                          </ToolTip>
                        )}
                        {order.referenceDetailRedFlag && (
                          <div className="orange-flag">
                            <i className="bi bi-flag-fill"></i>
                          </div>
                        )}
                        {order.criminalDetailRedFlag && (
                          <div className="blue-flag">
                            <i className="bi bi-flag-fill"></i>
                          </div>
                        )}
                      </div>
                      <div
                        className="chip-date chip-hidden"
                        style={{ gap: "10px", width: "240px" }}
                      >
                        <div>Forms Sent:</div>
                        <div>
                          <b>
                            {order.initialFormsDate
                              ? new Date(
                                  order.initialFormsDate,
                                ).toLocaleDateString()
                              : "N/A"}
                          </b>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-sub-header">No orders.</div>
          )}
          {loading && pageNumber > 1 && (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <div className="loading-spinner-small"></div>
            </div>
          )}
        </>
      )}
      {errorMessage && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default PendingOrders;
